// Evidence lanes: the analyst-facing grouping of every finding.
//
// A lane is derived from the collector that produced the evidence, never
// guessed in the UI, so the same finding always lands in the same section and
// the provenance chain (source -> url -> lane) stays intact end to end.

import type { CollectorRecord } from "@/lib/intel/collectors-extra";

export type Lane =
  | "social"
  | "contacts"
  | "documents"
  | "leaks"
  | "infrastructure"
  | "adverse"
  | "other";

export const LANE_LABEL: Record<Lane, string> = {
  social: "Social profiles",
  contacts: "Contacts",
  documents: "Documents & registry records",
  leaks: "Leaks & breach exposure",
  infrastructure: "Infrastructure",
  adverse: "Adverse media & legal",
  other: "Everything else",
};

export const LANE_ORDER: Lane[] = [
  "social",
  "contacts",
  "documents",
  "leaks",
  "infrastructure",
  "adverse",
  "other",
];

const SOCIAL_HOSTS = [
  "facebook.com", "instagram.com", "tiktok.com", "threads.net", "threads.com", "linkedin.com",
  "x.com", "twitter.com", "youtube.com", "snapchat.com", "pinterest.com", "reddit.com",
  "t.me", "telegram.me", "vk.com", "ok.ru", "tumblr.com", "flickr.com", "twitch.tv",
  "bsky.app", "mastodon.social", "mastodon.online", "medium.com", "substack.com",
  "github.com", "gitlab.com", "keybase.io", "gravatar.com", "about.me", "quora.com",
  "discord.com", "patreon.com", "soundcloud.com", "spotify.com", "myspace.com",
];

/** Platform label for a public profile URL, or null when it isn't one. */
export function socialPlatform(url: string): string | null {
  let host: string;
  try {
    host = new URL(url).hostname.replace(/^www\./, "").toLowerCase();
  } catch {
    return null;
  }
  const match = SOCIAL_HOSTS.find((h) => host === h || host.endsWith(`.${h}`));
  if (!match) return null;
  const base = match.split(".")[0]!;
  const pretty: Record<string, string> = {
    x: "X", vk: "VK", ok: "OK.ru", t: "Telegram", bsky: "Bluesky", tiktok: "TikTok",
    linkedin: "LinkedIn", youtube: "YouTube", github: "GitHub", gitlab: "GitLab",
  };
  return pretty[base] ?? base.charAt(0).toUpperCase() + base.slice(1);
}

/** Which lane a collector's evidence belongs to. */
export function laneForSource(source: string): Lane {
  const s = source.toLowerCase();
  if (/contact graph|permutation|mailbox|gravatar|libravatar|unavatar/.test(s)) return "contacts";
  if (/social|facebook|instagram|tiktok|linkedin|threads|snapchat|handle mesh|username|profile|reddit|mastodon|bluesky|telegram|youtube|twitter|\bx\b/.test(s)) return "social";
  if (/breach|pwned|leak|darknet|paste|combolist|stealer|ransom/.test(s)) return "leaks";
  if (/sanction|ofac|wanted|court|docket|litigation|adverse|criminal|police|enforcement|regulator|debarment/.test(s)) return "adverse";
  if (/dns|rdap|whois|certificate|\bct\b|crt\.sh|subdomain|bgp|asn|netblock|ip |geoloc|wayback|common crawl|infrastructure|shodan|tls|smtp|mx\b/.test(s)) return "infrastructure";
  if (/edgar|opencorporates|gleif|registry|registries|prh|ytj|companies|patent|crossref|openalex|arxiv|govinfo|gazette|tender|procurement|document|filing|annual report/.test(s)) return "documents";
  if (/news|gdelt|media|press|wikipedia|wikidata|rss/.test(s)) return "other";
  return "other";
}

/** First usable https URL inside a collector payload. */
export function firstUrl(c: CollectorRecord): string | undefined {
  const direct = (c.data as any)?.url;
  if (typeof direct === "string" && /^https?:\/\//.test(direct)) return direct;
  const hits = (c.data as any)?.hits;
  if (Array.isArray(hits)) {
    for (const h of hits) if (typeof h?.url === "string" && /^https?:\/\//.test(h.url)) return h.url;
  }
  let text = "";
  try {
    text = JSON.stringify(c.data ?? "").slice(0, 20_000);
  } catch {
    return undefined;
  }
  const m = text.match(/https?:\/\/[^\s"'\\<>()]{6,300}/);
  return m?.[0];
}

export type SocialProfile = {
  platform: string;
  url: string;
  /** Independent hosts/probes that produced this profile. */
  corroboration: number;
  sources: string[];
  verdict: "confirmed" | "likely";
  handle?: string;
};

/**
 * Build the social lane from everything a scan collected: the contact graph's
 * corroborated profile list plus every collector record that resolved to a
 * public profile URL. Deduped per platform+handle.
 */
export function buildSocialLane(collectors: CollectorRecord[]): SocialProfile[] {
  const byKey = new Map<string, SocialProfile>();

  const add = (url: string, sources: string[], corroboration: number) => {
    const platform = socialPlatform(url);
    if (!platform) return;
    let clean = url.split("#")[0]!.replace(/\/$/, "");
    let handle: string | undefined;
    try {
      const u = new URL(clean);
      const seg = u.pathname.split("/").filter(Boolean);
      handle = seg[seg.length - 1]?.replace(/^@/, "");
      clean = `${u.origin}${u.pathname.replace(/\/$/, "")}`;
    } catch {
      return;
    }
    // Bare platform landing pages carry no identity.
    if (!handle || handle.length < 2) return;
    if (/^(login|signup|home|about|help|privacy|terms|explore|search|share|intent|sharer)$/i.test(handle)) return;
    const key = `${platform.toLowerCase()}::${handle.toLowerCase()}`;
    const prev = byKey.get(key);
    const merged: SocialProfile = {
      platform,
      url: prev?.url ?? clean,
      handle,
      corroboration: (prev?.corroboration ?? 0) + corroboration,
      sources: Array.from(new Set([...(prev?.sources ?? []), ...sources])).slice(0, 8),
      verdict: "likely",
    };
    merged.verdict = merged.corroboration >= 2 || merged.sources.length >= 2 ? "confirmed" : "likely";
    byKey.set(key, merged);
  };

  for (const c of collectors) {
    if (c.status !== "ok") continue;
    const data = c.data as any;
    if (c.source === "Contact graph" && Array.isArray(data?.profiles)) {
      for (const p of data.profiles) {
        if (typeof p?.value === "string") add(p.value, p.sources ?? [c.source], Math.max(1, p.corroboration ?? 1));
      }
      continue;
    }
    const url = firstUrl(c);
    if (url) add(url, [c.source], 1);
    if (Array.isArray(data?.hits)) {
      for (const h of data.hits) if (typeof h?.url === "string") add(h.url, [c.source], 1);
    }
  }

  return Array.from(byKey.values()).sort(
    (a, b) => b.corroboration - a.corroboration || a.platform.localeCompare(b.platform),
  );
}
